import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useMemo,
} from "react";

export interface DateRange {
  from: Date;
  to: Date;
  label: string;
}

const EARLIEST_DATA_DATE = new Date(2025, 2, 1); // March 1, 2025

const endOfMonth = (date: Date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  // Get the last day of the month by going to day 0 of next month
  const lastDay = new Date(year, month + 1, 0).getDate();
  // Return date set to last day of current month at end of day
  return new Date(year, month, lastDay, 23, 59, 59, 999);
};

const formatMonthLabel = (date: Date) =>
  date.toLocaleString("default", { month: "long", year: "numeric" });

export const generatePresetDateRanges = (
  referenceDate: Date = new Date(),
): DateRange[] => {
  const now = referenceDate;
  const allEnd = endOfMonth(now);
  const earliest = new Date(
    EARLIEST_DATA_DATE.getFullYear(),
    EARLIEST_DATA_DATE.getMonth(),
    1,
  );

  const presets: DateRange[] = [
    {
      from: earliest,
      to: allEnd,
      label: "All Available Data",
    },
  ];

  let cursor = new Date(earliest);
  while (cursor <= allEnd) {
    const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const monthEnd = endOfMonth(monthStart);
    presets.push({
      from: monthStart,
      to: monthEnd,
      label: formatMonthLabel(monthStart),
    });
    cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
  }

  const last30Start = new Date(now);
  last30Start.setDate(last30Start.getDate() - 29);
  const last7Start = new Date(now);
  last7Start.setDate(last7Start.getDate() - 6);

  presets.push(
    {
      from: last30Start,
      to: now,
      label: "Last 30 Days",
    },
    {
      from: last7Start,
      to: now,
      label: "Last 7 Days",
    },
  );

  return presets;
};

interface DateRangeContextType {
  dateRange: DateRange;
  setDateRange: (range: DateRange) => void;
  setCustomDateRange: (from: Date, to: Date) => void;
  selectedLocations: string[];
  setSelectedLocations: (locations: string[]) => void;
  toggleLocation: (location: string) => void;
  clearFilters: () => void;
  presetRanges: DateRange[];
}

const DateRangeContext = createContext<DateRangeContextType | undefined>(
  undefined,
);

interface DateRangeProviderProps {
  children: ReactNode;
}

export function DateRangeProvider({ children }: DateRangeProviderProps) {
  const presetRanges = useMemo(() => generatePresetDateRanges(), []);

  const getDefaultRange = () => {
    const stored = localStorage.getItem("globalDateRange");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return {
          ...parsed,
          from: new Date(parsed.from),
          to: new Date(parsed.to),
        } as DateRange;
      } catch (e) {
        console.error("Failed to parse stored date range:", e);
      }
    }
    return presetRanges[0];
  };

  // Initialize with Year to Date or load from localStorage
  const [dateRange, setDateRangeState] = useState<DateRange>(getDefaultRange);

  // Initialize selected locations from localStorage
  const [selectedLocations, setSelectedLocationsState] = useState<string[]>(
    () => {
      const stored = localStorage.getItem("globalSelectedLocations");
      if (stored) {
        try {
          return JSON.parse(stored);
        } catch (e) {
          console.error("Failed to parse stored locations:", e);
        }
      }
      return []; // Default to all locations (empty array means no filter)
    },
  );

  // Persist to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("globalDateRange", JSON.stringify(dateRange));
  }, [dateRange]);

  useEffect(() => {
    localStorage.setItem(
      "globalSelectedLocations",
      JSON.stringify(selectedLocations),
    );
  }, [selectedLocations]);

  const setDateRange = (range: DateRange) => {
    setDateRangeState(range);
  };

  const setCustomDateRange = (from: Date, to: Date) => {
    setDateRangeState({
      from,
      to,
      label: "Custom Range",
    });
  };

  const setSelectedLocations = (locations: string[]) => {
    setSelectedLocationsState(locations);
  };

  const toggleLocation = (location: string) => {
    setSelectedLocationsState((prev) =>
      prev.includes(location)
        ? prev.filter((l) => l !== location)
        : [...prev, location],
    );
  };

  const clearFilters = () => {
    setDateRangeState(presetRanges[0]);
    setSelectedLocationsState([]);
  };

  return (
    <DateRangeContext.Provider
      value={{
        dateRange,
        setDateRange,
        setCustomDateRange,
        selectedLocations,
        setSelectedLocations,
        toggleLocation,
        clearFilters,
        presetRanges,
      }}
    >
      {children}
    </DateRangeContext.Provider>
  );
}

export function useDateRange() {
  const context = useContext(DateRangeContext);
  if (context === undefined) {
    throw new Error("useDateRange must be used within a DateRangeProvider");
  }
  return context;
}
